/**
 * 对话工作流 / Plus 首页：输入框内联参考图（与 ConversationWorkflow 一致）
 */

export function insertUploadInlineAtCursor(composerEl, u) {
  const el = composerEl
  if (!el || !u || !u.url) return

  const sel = window.getSelection ? window.getSelection() : null
  if (!sel) return
  if (!sel.rangeCount) {
    const r = document.createRange()
    r.selectNodeContents(el)
    r.collapse(false)
    sel.removeAllRanges()
    sel.addRange(r)
  }

  const range = sel.getRangeAt(0)
  if (!el.contains(range.commonAncestorContainer)) {
    range.selectNodeContents(el)
    range.collapse(false)
  }

  const wrap = document.createElement('span')
  wrap.setAttribute('data-cw-upload', '1')
  wrap.setAttribute('data-cw-url', u && u.url ? String(u.url) : '')
  wrap.setAttribute('data-cw-kind', u && u.kind === 'video' ? 'video' : 'image')
  wrap.setAttribute('data-cw-name', u && u.name ? String(u.name) : '')
  wrap.contentEditable = 'false'
  wrap.className = 'cw-upload-chip inline-flex items-center align-middle mx-1 my-0.5'
  wrap.title = (u && u.name ? String(u.name) : '') || (u && u.kind === 'video' ? '视频' : '图片')

  if (u && u.kind === 'image') {
    const img = document.createElement('img')
    img.src = u.url
    img.alt = ''
    img.className = 'w-8 h-8 rounded-xl object-cover'
    wrap.appendChild(img)
  } else {
    const box = document.createElement('span')
    box.className = 'relative inline-block w-8 h-8 rounded-xl overflow-hidden bg-black/40'

    const vid = document.createElement('video')
    vid.src = u && u.url ? String(u.url) : ''
    vid.muted = true
    vid.playsInline = true
    vid.preload = 'metadata'
    vid.className = 'w-8 h-8 object-cover opacity-90'
    box.appendChild(vid)

    const overlay = document.createElement('span')
    overlay.className = 'absolute inset-0 flex items-center justify-center text-white/75'
    overlay.innerHTML = '<i class="fas fa-play-circle text-[12px]"></i>'
    box.appendChild(overlay)

    wrap.appendChild(box)
  }

  range.insertNode(wrap)
  const space = document.createTextNode(' ')
  wrap.after(space)
  const nr = document.createRange()
  nr.setStartAfter(space)
  nr.collapse(true)
  sel.removeAllRanges()
  sel.addRange(nr)
}

export function syncReferencedUploadsFromComposer(composerEl) {
  const el = composerEl
  if (!el || !el.querySelectorAll) return []
  const nodes = Array.from(el.querySelectorAll('[data-cw-upload="1"][data-cw-url]') || [])
  const refs = []
  for (const n of nodes) {
    const url = String(n.getAttribute('data-cw-url') || '').trim()
    if (!url) continue
    const kind = String(n.getAttribute('data-cw-kind') || '').trim() === 'video' ? 'video' : 'image'
    const name = String(n.getAttribute('data-cw-name') || '').trim()
    refs.push({ kind, url, name })
  }
  return refs
}

export function getComposerPlainText(composerEl) {
  const el = composerEl
  const txt = el && el.innerText != null ? String(el.innerText) : ''
  return txt.replace(/\u00a0/g, ' ')
}

export function getComposerHtml(composerEl) {
  const el = composerEl
  return el ? String(el.innerHTML || '') : ''
}

export function composerHasInlineUploads(composerEl) {
  const el = composerEl
  return !!(el && el.querySelector && el.querySelector('[data-cw-upload="1"]'))
}

export function isComposerEmpty(composerEl, plainText) {
  const text = (plainText || '').trim()
  return !text && !composerHasInlineUploads(composerEl)
}

export function removeInlineUploadByUrl(composerEl, url) {
  const el = composerEl
  const u = url != null ? String(url) : ''
  if (!el || !u) return
  const nodes = el.querySelectorAll ? el.querySelectorAll('[data-cw-upload="1"][data-cw-url]') : []
  for (const n of nodes || []) {
    try {
      if (String(n.getAttribute('data-cw-url') || '') === u) n.remove()
    } catch (e) {
      /* ignore */
    }
  }
}

export function rewriteInlineUploadUrl(composerEl, oldUrl, newUrl) {
  const el = composerEl
  const from = oldUrl != null ? String(oldUrl) : ''
  const to = newUrl != null ? String(newUrl) : ''
  if (!el || !from || !to || from === to) return
  const nodes = el.querySelectorAll ? el.querySelectorAll('[data-cw-upload="1"][data-cw-url]') : []
  for (const n of nodes || []) {
    if (String(n.getAttribute('data-cw-url') || '') !== from) continue
    n.setAttribute('data-cw-url', to)
    const img = n.querySelector && n.querySelector('img')
    if (img) img.src = to
    const vid = n.querySelector && n.querySelector('video')
    if (vid) vid.src = to
  }
}

export function hasInlineUploadByUrl(composerEl, url) {
  const el = composerEl
  const u = url != null ? String(url) : ''
  if (!el || !u) return false
  try {
    return !!el.querySelector(`[data-cw-upload="1"][data-cw-url="${CSS.escape(u)}"]`)
  } catch (e) {
    const nodes = el.querySelectorAll ? el.querySelectorAll('[data-cw-upload="1"][data-cw-url]') : []
    for (const n of nodes || []) {
      if (String(n.getAttribute('data-cw-url') || '') === u) return true
    }
    return false
  }
}

/**
 * Backspace/Delete 删除内联引用块（与对话工作流一致）
 * @returns {boolean} 是否已处理该按键
 */
export function handleComposerUploadKeydown(e, composerEl, onAfterChange) {
  if (!e || !composerEl) return false
  if ((e.key !== 'Backspace' && e.key !== 'Delete') || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) {
    return false
  }

  const el = composerEl
  const sel = window.getSelection ? window.getSelection() : null
  if (!el || !sel || !sel.rangeCount) return false

  const range = sel.getRangeAt(0)

  const removeNodeIfUpload = (node) => {
    if (!node) return false
    let n = node
    if (n.nodeType !== 1 && n.parentNode) n = n.parentNode
    const isElem = n && n.nodeType === 1
    const wrap = isElem && n.getAttribute && n.getAttribute('data-cw-upload') === '1' ? n : null
    if (!wrap) return false
    try {
      wrap.remove()
    } catch (err) {
      try {
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap)
      } catch (err2) {
        /* ignore */
      }
    }
    if (onAfterChange) onAfterChange()
    return true
  }

  if (!range.collapsed) {
    const frag = range.cloneContents()
    const hasUpload =
      frag && frag.querySelector && !!frag.querySelector('[data-cw-upload="1"][data-cw-url]')
    if (hasUpload) {
      e.preventDefault()
      range.deleteContents()
      if (onAfterChange) onAfterChange()
      return true
    }
    return false
  }

  const container = range.startContainer
  const offset = range.startOffset

  if (container && container.nodeType === 3) {
    if (e.key === 'Backspace' && offset === 0) {
      const prev = container.previousSibling
      if (removeNodeIfUpload(prev)) {
        e.preventDefault()
        return true
      }
    }
    if (e.key === 'Delete' && offset === (container.nodeValue || '').length) {
      const next = container.nextSibling
      if (removeNodeIfUpload(next)) {
        e.preventDefault()
        return true
      }
    }
  }

  if (container && container.nodeType === 1) {
    const child = container.childNodes ? container.childNodes[offset] : null
    if (e.key === 'Delete' && removeNodeIfUpload(child)) {
      e.preventDefault()
      return true
    }
    if (e.key === 'Backspace') {
      const prev = container.childNodes ? container.childNodes[offset - 1] : null
      if (removeNodeIfUpload(prev)) {
        e.preventDefault()
        return true
      }
    }
  }

  return false
}
